// Streamované modely v SDK — interní podpora pro IFCViewer.loadStream().
//
// Geometrie se načítá po dlaždicích (That Open IfcStreamer) z artefaktů ingestu
// `<soubor>.ifc.stream/` (streamed-geometries.json + *.bin). Metadata pro identitu
// prvků, podlaží a strom dodává light-index (light-{storeys,elements,tree}.json, u
// starších artefaktů sloučený light-index.json). Klient tak IFC neparsuje a paměť
// zůstává omezená i u velkých modelů. Veřejný kontrakt {modelId, globalId} se nemění:
// GlobalId → expressID (= assetID dlaždic) se překládá přes light-index.
//
// Modul je čistě interní (nevystavuje se z index.js). Pracuje nad stavem fasády
// (viewer._models, _hidden, _isolated, _colorGroups, _selection), aby se streamované
// a klasicky načtené modely ve federaci chovaly jednotně.
import * as OBCF from "@thatopen/components-front";
import * as FRAG from "@thatopen/fragments";
import * as THREE from "three";
import { ErrorCode, ViewerError } from "./errors.js";

// Artefakty se načítají se stejnou autorizací jako model (cookies same-origin).
const FETCH = { credentials: "same-origin" };
// Manifest je malý řídicí soubor — vždy znovu ověřit, aby dřívější záporná odpověď
// (např. během publikace) nedržela model trvale bez streamu.
const MANIFEST_FETCH = { credentials: "same-origin", cache: "reload" };
const SPATIAL_TYPES = new Set(["IFCPROJECT", "IFCSITE", "IFCBUILDING", "IFCBUILDINGSTOREY", "IFCSPACE"]);
// Kolik obarvených prvků se smí natrvalo dotáhnout (static), aby barva byla vidět
// i mimo aktuální pohled. Nad limitem se barví jen právě načtená geometrie.
const STATIC_COLOR_LIMIT = 5000;
const OVERLAY_REFRESH_MS = 250;

// Normalizuje bázovou URL streamu na absolutní href končící "/". Odmítne URL s
// přihlašovacími údaji, dotazem nebo fragmentem i jiné protokoly než http(s).
export function normalizeStreamBase(value) {
  let url;
  try { url = new URL(String(value || ""), typeof location !== "undefined" ? location.href : undefined); }
  catch (_) { throw new ViewerError(ErrorCode.INVALID_ARGUMENT, "Neplatná adresa streamu.", { details: { base: value } }); }
  if (!/^https?:$/.test(url.protocol) || url.username || url.password || url.search || url.hash || !value) {
    throw new ViewerError(ErrorCode.INVALID_ARGUMENT, "Neplatná adresa streamu.", { details: { base: value } });
  }
  if (!url.pathname.endsWith("/")) url.pathname += "/";
  return url.href;
}

// Soubor z manifestu musí ležet uvnitř adresáře streamu (ochrana proti ../ a cizím
// originům v manifestu). Vrací absolutní href.
function scopedUrl(baseHref, file) {
  const base = new URL(baseHref);
  const url = new URL(String(file || ""), base);
  if (url.origin !== base.origin || !url.pathname.startsWith(base.pathname) || url.search || url.hash) {
    throw new ViewerError(ErrorCode.INVALID_ARGUMENT, "Manifest streamu odkazuje mimo adresář streamu.", { details: { file } });
  }
  return url.href;
}

function scopeGeometrySettings(settings, baseHref) {
  if (!settings || !settings.geometries || !Array.isArray(settings.assets) || !settings.globalDataFileId) {
    throw new ViewerError(ErrorCode.IFC_LOAD_FAILED, "Neplatný manifest streamu.");
  }
  const files = new Map();
  for (const geometry of Object.values(settings.geometries)) {
    const file = geometry.geometryFile;
    if (!files.has(file)) files.set(file, scopedUrl(baseHref, file));
    geometry.geometryFile = files.get(file);
  }
  settings.globalDataFileId = scopedUrl(baseHref, settings.globalDataFileId);
  return settings;
}

async function fetchJson(url) {
  try {
    const response = await fetch(url, FETCH);
    if (response.ok) return await response.json();
  } catch (_) { /* volitelný artefakt */ }
  return null;
}

// Light-index: split soubory (nový ingest), jinak sloučený light-index.json.
async function loadLight(baseHref) {
  const [storeys, elements, tree] = await Promise.all([
    fetchJson(baseHref + "light-storeys.json"),
    fetchJson(baseHref + "light-elements.json"),
    fetchJson(baseHref + "light-tree.json"),
  ]);
  const light = {
    storeys: storeys && Array.isArray(storeys.storeys) ? storeys.storeys : null,
    unitScale: storeys ? storeys.unitScale : null,
    elements: elements && elements.elements ? elements.elements : null,
    tree: tree && Array.isArray(tree.tree) ? tree.tree : null,
  };
  if (!light.storeys || !light.elements || !light.tree) {
    const combined = await fetchJson(baseHref + "light-index.json");
    if (combined) {
      if (!light.storeys) light.storeys = combined.storeys || [];
      if (!light.elements) light.elements = combined.elements || {};
      if (!light.tree) light.tree = combined.tree || [];
      if (light.unitScale == null) light.unitScale = combined.unitScale;
    }
  }
  light.storeys ||= [];
  light.elements ||= {};
  light.tree ||= [];
  return light;
}

// Obousměrný index GlobalId ↔ expressID + třída a podlaží prvku z light-indexu.
function buildStreamIndex(group, light) {
  const globalToExpress = new Map();
  const expressToGlobal = new Map();
  const classOf = new Map();       // expressID → IFC třída (velkými písmeny, jako IfcCategoryMap)
  const nameOf = new Map();        // expressID → Name z light-indexu
  const add = (globalId, expressId) => {
    const id = Number(expressId);
    if (!globalId || !Number.isFinite(id)) return;
    if (!globalToExpress.has(globalId)) globalToExpress.set(globalId, id);
    if (!expressToGlobal.has(id)) expressToGlobal.set(id, globalId);
  };
  try {
    if (group.globalToExpressIDs instanceof Map) for (const [gid, eid] of group.globalToExpressIDs) add(gid, eid);
  } catch (_) {}
  for (const [globalId, info] of Object.entries(light.elements || {})) {
    if (!info) continue;
    add(globalId, info.expressID);
    const id = Number(info.expressID);
    if (info.ifcClass) classOf.set(id, String(info.ifcClass).toUpperCase());
    if (info.name) nameOf.set(id, info.name);
  }
  // Prostorové prvky (projekt, budova, podlaží, místnosti) jsou ve stromu — BCF
  // viewpointy na ně mohou odkazovat.
  const stack = [...(light.tree || [])];
  while (stack.length) {
    const node = stack.pop();
    if (!node) continue;
    if (node.expressID != null) {
      const id = Number(node.expressID);
      if (node.globalId) add(node.globalId, id);
      if (node.type && !classOf.has(id)) classOf.set(id, String(node.type).toUpperCase());
      if (node.name && !nameOf.has(id)) nameOf.set(id, node.name);
    }
    for (const child of node.children || []) stack.push(child);
  }
  return { ids: { globalToExpress, expressToGlobal }, classOf, nameOf };
}

function val(v) {
  if (v === null || v === undefined) return null;
  if (typeof v === "object") return "value" in v ? (v.value == null ? null : v.value) : null;
  return v;
}

export class StreamSupport {
  constructor(core, viewer) {
    this.core = core;
    this.viewer = viewer;
    this.streamer = null;
    this._pickingWired = false;
    this._colorOverlay = null;         // THREE.Group s barevnými klony instancí
    this._colorMaterials = new Map();  // hex → MeshBasicMaterial
    this._staticSelection = new Set(); // fragmentID trvale dotažené kvůli výběru
    this._staticColor = new Set();     // fragmentID trvale dotažené kvůli barvám
    this._appliedStatic = new Set();
    this._refreshTimer = null;
    this._raycaster = new THREE.Raycaster();
  }

  // --- Streamer (jedna instance na viewer, obsluhuje všechny streamované modely) ---
  ensureStreamer() {
    if (this.streamer) return this.streamer;
    const streamer = this.core.components.get(OBCF.IfcStreamer);
    streamer.world = this.core.world;
    // Manifest nese absolutní cesty; jedna instance tak obslouží více adresářů.
    streamer.url = "";
    streamer.useCache = false;
    streamer.fetch = (path) => fetch(path, FETCH);
    // Kvalita ↔ výkon a retence dlaždic shodně s hlavním viewerem (ověřeno na velkých
    // federovaných modelech): drobná geometrie nad 3 px, delší držení v RAM proti
    // výpadkům při orbitu, jemnější cull buffer bez děr při zoomu.
    const culler = streamer.culler;
    culler.threshold = 3;
    culler.maxHiddenTime = 30000;
    culler.maxLostTime = 60000;
    streamer.maxRamTime = 30000;
    try { culler.config.width = 2048; culler.config.height = 2048; } catch (_) {}
    try { culler.config.updateInterval = 100; } catch (_) {}
    const bump = () => { culler.needsUpdate = true; };
    const controls = this.core.world.camera.controls;
    if (controls && controls.addEventListener) {
      controls.addEventListener("rest", bump);
      controls.addEventListener("control", bump);
    }
    if (streamer.onFragmentsLoaded) streamer.onFragmentsLoaded.add(() => this.scheduleRefresh());
    this.streamer = streamer;
    return streamer;
  }

  // Načte streamovaný model. Vrací { group, light, index, box, settings }.
  async load(baseHref) {
    const streamer = this.ensureStreamer();
    let rawSettings;
    try {
      const response = await fetch(baseHref + "streamed-geometries.json", MANIFEST_FETCH);
      if (!response.ok) {
        throw new ViewerError(ErrorCode.NETWORK_ERROR, "HTTP " + response.status + " při načítání manifestu streamu.", { details: { status: response.status, url: baseHref } });
      }
      rawSettings = await response.json();
    } catch (e) {
      if (e instanceof ViewerError) throw e;
      throw new ViewerError(ErrorCode.NETWORK_ERROR, "Síťová chyba při načítání manifestu streamu.", { cause: e, details: { url: baseHref } });
    }
    const settings = scopeGeometrySettings(rawSettings, baseHref);

    // Federační offset: první model federace určuje rámec. Streamovaný model zůstává
    // v souřadnicích ingestu (posun k počátku nese coordinationMatrix), takže pokud je
    // první, offset je nulový — stejně jako v hlavním vieweru.
    const core = this.core;
    if (!core.federationOffsetInit) { core.federationOffset.set(0, 0, 0); core.federationOffsetInit = true; }

    const group = await streamer.load(settings, true);
    try {
      if (core.federationOffset.lengthSq() > 0) {
        group.position.add(core.federationOffset);
        group.updateMatrixWorld(true);
        streamer.culler.updateTransformations(group.uuid);
      }
      const light = await loadLight(baseHref);
      const index = buildStreamIndex(group, light);
      const box = await this.modelBoxFromCuller(group.uuid);
      streamer.culler.needsUpdate = true;
      return { group, light, index, box, settings };
    } catch (e) {
      this.disposeGroup(group);
      throw e;
    }
  }

  // FragmentsManager.disposeGroup spustí i úklid streameru (disposeStreamedGroup).
  disposeGroup(group) {
    this.core.disposeGroup(group);
  }

  // Bbox modelu z instančních boxů culleru (nezávisí na tom, co je právě načteno).
  // Culler plní matice instancí až po vytvoření boxů — dokud nejsou platné, čekáme.
  async modelBoxFromCuller(uuid) {
    for (let i = 0; i < 30; i++) {
      const box = this.computeModelBox(uuid);
      if (box) return box;
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
    return null;
  }

  computeModelBox(uuid) {
    const culler = this.streamer && this.streamer.culler;
    if (!culler) return null;
    const modelIndex = culler._modelIDIndex && culler._modelIDIndex.get(uuid);
    const frag = modelIndex == null ? null : culler.boxes.get(modelIndex);
    const mesh = frag && (frag.mesh || frag);
    const count = mesh ? (mesh.count || (mesh.instanceMatrix ? mesh.instanceMatrix.count : 0)) : 0;
    if (!mesh || !count) return null;
    mesh.updateMatrixWorld(true);
    const corners = [[0, 0, 0], [1, 0, 0], [0, 1, 0], [0, 0, 1], [1, 1, 0], [1, 0, 1], [0, 1, 1], [1, 1, 1]];
    const m = new THREE.Matrix4(), p = new THREE.Vector3();
    const box = new THREE.Box3();
    const step = Math.max(1, Math.floor(count / 8000));
    for (let i = 0; i < count; i += step) {
      mesh.getMatrixAt(i, m);
      for (const c of corners) {
        p.set(c[0], c[1], c[2]).applyMatrix4(m).applyMatrix4(mesh.matrixWorld);
        if (Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z)) box.expandByPoint(p);
      }
    }
    return box.isEmpty() ? null : box;
  }

  // --- Mapy fragmentů ---------------------------------------------------------
  // FragmentIdMap pro dané expressID. Obsahuje i dosud nenačtené dlaždice (keyFragments
  // jsou známé od načtení manifestu); filtrujeme jen na fragmenty, které streamer zná.
  fragmentMap(model, expressIds) {
    let map = {};
    try { map = model.group.getFragmentMap(expressIds) || {}; } catch (_) { map = {}; }
    const known = this.streamer && this.streamer.fragIDData;
    if (!known) return map;
    for (const fragID of Object.keys(map)) if (!known.has(fragID)) delete map[fragID];
    return map;
  }

  allAssetIds(model) {
    const entry = this.streamer && this.streamer.models[model.group.uuid];
    return entry ? Array.from(entry.assetsMap.keys(), Number) : [];
  }

  // --- Viditelnost --------------------------------------------------------------
  // Skládá viditelnost z modelu, izolace a skrytých prvků fasády. streamer.setVisibility
  // nastaví culler (i pro nenačtené dlaždice), právě načtené fragmenty a trvalou
  // množinu skrytých prvků, kterou streamer uplatní i na později dotažené dlaždice.
  applyVisibility(model) {
    if (!this.streamer || !model || !model.streamed) return;
    const all = this.allAssetIds(model);
    if (!all.length) return;
    const isShown = this._shownPredicate(model);
    const visible = [], hidden = [];
    for (const id of all) {
      const show = model.visible !== false && isShown(id);
      (show ? visible : hidden).push(id);
    }
    try {
      if (hidden.length) {
        const map = this.fragmentMap(model, hidden);
        if (Object.keys(map).length) this.streamer.setVisibility(false, map);
      }
      if (visible.length) {
        const map = this.fragmentMap(model, visible);
        if (Object.keys(map).length) this.streamer.setVisibility(true, map);
      }
    } catch (_) { /* culler zůstává autoritativní pro nenačtené dlaždice */ }
    this.streamer.culler.needsUpdate = true;
  }

  applyAllVisibility() {
    for (const model of this.viewer._models.values()) if (model.streamed) this.applyVisibility(model);
    this.scheduleRefresh();
  }

  // --- Ohraničení -----------------------------------------------------------------
  boxForExpress(model, expressIds) {
    if (!this.streamer) return null;
    let boxes = null;
    try {
      const map = this.fragmentMap(model, expressIds);
      if (!Object.keys(map).length) return null;
      boxes = this.streamer.getBoundingBoxes(map);
      boxes.updateMatrixWorld(true);
      const box = new THREE.Box3().setFromObject(boxes);
      return box.isEmpty() ? null : box;
    } catch (_) { return null; }
    finally { try { if (boxes) boxes.dispose(false); } catch (_) {} }
  }

  // --- Trvale dotažené (static) dlaždice pro výběr a barvy -------------------------
  async setStaticSelection(entries) {
    this._staticSelection = this._fragmentIdsFor(entries);
    await this._syncStatic();
  }
  async _setStaticColor(entries) {
    this._staticColor = this._fragmentIdsFor(entries);
    await this._syncStatic();
  }
  _fragmentIdsFor(entries) {
    const out = new Set();
    for (const { model, expressIds } of entries || []) {
      if (!model || !model.streamed || !expressIds.length) continue;
      for (const fragID of Object.keys(this.fragmentMap(model, expressIds))) out.add(fragID);
    }
    return out;
  }
  async _syncStatic() {
    if (!this.streamer) return;
    const wanted = new Set([...this._staticSelection, ...this._staticColor]);
    const remove = new Set([...this._appliedStatic].filter((id) => !wanted.has(id)));
    const add = new Set([...wanted].filter((id) => !this._appliedStatic.has(id)));
    this._appliedStatic = wanted;
    try { if (remove.size) await this.streamer.setStatic(remove, false, false); } catch (_) {}
    try { if (add.size) await this.streamer.setStatic(add, true, false); } catch (_) {}
  }

  // --- Barevný overlay ------------------------------------------------------------
  // Barvy streamovaných prvků kreslíme jako klony instancí přes geometrii (polygon
  // offset → bez z-fightingu, přitom zakryté prvky zůstávají zakryté). Po dotažení
  // dalších dlaždic se overlay přestaví.
  _colorMaterial(hex) {
    let material = this._colorMaterials.get(hex);
    if (!material) {
      material = new THREE.MeshBasicMaterial({
        color: new THREE.Color(parseInt(hex, 16)), transparent: true, opacity: 0.85,
        side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -4,
      });
      this._colorMaterials.set(hex, material);
    }
    return material;
  }

  _clearColorOverlay() {
    const group = this._colorOverlay;
    if (!group) return;
    const disposed = new Set();
    for (const mesh of group.children) {
      if (mesh.geometry && !disposed.has(mesh.geometry)) { disposed.add(mesh.geometry); mesh.geometry.dispose(); }
    }
    if (group.parent) group.parent.remove(group);
    this._colorOverlay = null;
  }

  // Seznam { model, expressIds } obarvených streamovaných prvků podle barvy.
  _colorEntries() {
    const viewer = this.viewer;
    const byHex = new Map();
    for (const [hex, keys] of viewer._colorGroups) {
      const perModel = new Map();
      for (const key of keys) {
        const sep = key.indexOf("|");
        const model = viewer._models.get(key.slice(0, sep));
        if (!model || !model.streamed) continue;
        if (!perModel.has(model)) perModel.set(model, []);
        perModel.get(model).push(Number(key.slice(sep + 1)));
      }
      if (perModel.size) byHex.set(hex, perModel);
    }
    return byHex;
  }

  _rebuildColorOverlay() {
    this._clearColorOverlay();
    const byHex = this._colorEntries();
    if (!byHex.size) return;
    const group = new THREE.Group();
    const geometryCache = new Map();
    const matrix = new THREE.Matrix4();
    for (const [hex, perModel] of byHex) {
      const material = this._colorMaterial(hex);
      for (const [model, expressIds] of perModel) {
        if (model.visible === false) continue;
        const isShown = this._shownPredicate(model);
        const wanted = new Set(expressIds.filter(isShown));
        if (!wanted.size) continue;
        for (const frag of model.group.items || []) {
          if (!frag || !frag.mesh || typeof frag.getInstancesIDs !== "function") continue;
          const candidates = frag.ids ? Array.from(frag.ids).filter((id) => wanted.has(Number(id))) : Array.from(wanted);
          if (!candidates.length) continue;
          let geometry = geometryCache.get(frag.mesh.geometry);
          if (!geometry) { geometry = frag.mesh.geometry.clone(); geometryCache.set(frag.mesh.geometry, geometry); }
          frag.mesh.updateMatrixWorld(true);
          for (const id of candidates) {
            const instances = frag.getInstancesIDs(Number(id));
            if (!instances || !instances.size) continue;
            for (const instance of instances) {
              frag.mesh.getMatrixAt(instance, matrix);
              const mesh = new THREE.Mesh(geometry, material);
              mesh.matrixAutoUpdate = false;
              mesh.matrix.copy(frag.mesh.matrixWorld).multiply(matrix);
              mesh.renderOrder = 998;
              group.add(mesh);
            }
          }
        }
      }
    }
    if (group.children.length) { this.core.world.scene.three.add(group); this._colorOverlay = group; }
  }

  // Predikát expressID → je prvek zobrazen podle izolace / skrytí ve fasádě.
  _shownPredicate(model) {
    const viewer = this.viewer;
    if (viewer._isolated) {
      const isolated = new Set();
      for (const ref of viewer._isolated) {
        if (ref.modelId !== model.modelId) continue;
        const eid = model.ids.globalToExpress.get(ref.globalId);
        if (eid != null) isolated.add(Number(eid));
      }
      return (id) => isolated.has(Number(id));
    }
    const prefix = model.modelId + "|";
    return (id) => !viewer._hidden.has(prefix + Number(id));
  }

  // Volá fasáda po změně barev: dotáhne obarvené prvky a překreslí overlay.
  async colorsChanged() {
    const entries = [];
    let total = 0;
    for (const perModel of this._colorEntries().values()) {
      for (const [model, expressIds] of perModel) { entries.push({ model, expressIds }); total += expressIds.length; }
    }
    await this._setStaticColor(total <= STATIC_COLOR_LIMIT ? entries : []);
    this._rebuildColorOverlay();
  }

  // Dotažení dlaždic / změna viditelnosti → přestavět overlay a obrys výběru (sloučeně).
  scheduleRefresh() {
    if (this._refreshTimer != null) clearTimeout(this._refreshTimer);
    this._refreshTimer = setTimeout(() => {
      this._refreshTimer = null;
      try { this._rebuildColorOverlay(); } catch (_) {}
      try {
        if (this.viewer._selection.some((s) => { const m = this.viewer._models.get(s.modelId); return m && m.streamed; })) {
          this.viewer._applySelectionOutline();
        }
      } catch (_) {}
    }, OVERLAY_REFRESH_MS);
  }

  // --- Výběr klikem ----------------------------------------------------------------
  // Highlighter That Open pracuje jen s klasickými fragmenty. Jakmile je ve scéně
  // streamovaný model, přebírá klikání vlastní raycast nad VŠEMI modely federace.
  wirePicking() {
    if (this._pickingWired) return;
    this._pickingWired = true;
    const viewer = this.viewer;
    const hl = this.core.highlighter;
    try { hl.config.autoHighlightOnClick = false; } catch (_) {}
    const container = this.core.container;
    if (!container) return;
    let downX = 0, downY = 0, downButton = 0;
    container.addEventListener("pointerdown", (e) => { downX = e.clientX; downY = e.clientY; downButton = e.button; });
    container.addEventListener("pointerup", (e) => {
      if (viewer._destroyed || !viewer._initialized || viewer._opts.selectionEnabled === false) return;
      if (downButton !== 0 || e.button !== 0) return;
      if (Math.hypot(e.clientX - downX, e.clientY - downY) > 5) return;   // tažení = orbit
      const canvas = this.core.world.renderer.three.domElement;
      if (e.target !== canvas) return;
      const hit = this.pick(e.clientX, e.clientY);
      viewer._commitSelection(hit ? [hit] : [], { interactive: true });
    });
  }

  pick(clientX, clientY) {
    const viewer = this.viewer;
    const canvas = this.core.world.renderer.three.domElement;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const ndc = new THREE.Vector2(((clientX - rect.left) / rect.width) * 2 - 1, -((clientY - rect.top) / rect.height) * 2 + 1);
    const camera = this.core.world.camera.three;
    this._raycaster.setFromCamera(ndc, camera);
    const meshes = [];
    for (const model of viewer._models.values()) {
      if (model.visible === false || !model.group || model.group.visible === false) continue;
      for (const frag of model.group.items || []) {
        if (frag && frag.mesh && frag.mesh.visible !== false) meshes.push(frag.mesh);
      }
    }
    if (!meshes.length) return null;
    const planes = (this.core.world.renderer.three.clippingPlanes || []);
    const hits = this._raycaster.intersectObjects(meshes, false);
    for (const hit of hits) {
      if (hit.instanceId == null) continue;
      if (planes.some((plane) => plane.distanceToPoint(hit.point) < 0)) continue;   // odříznuto řezem
      const frag = hit.object.fragment;
      if (!frag || typeof frag.getItemID !== "function") continue;
      const expressId = frag.getItemID(hit.instanceId);
      if (expressId == null) continue;
      const model = viewer._modelOfGroup(frag.group);
      if (!model) continue;
      const globalId = model.ids.expressToGlobal.get(Number(expressId));
      if (!globalId) continue;
      return { modelId: model.modelId, globalId, expressId: Number(expressId), ifcClass: viewer._ifcClassSync(model, Number(expressId)) };
    }
    return null;
  }

  // Výběr se změnil → dotáhnout geometrii vybraných streamovaných prvků (obrys výběru
  // se po dotažení dokreslí přes scheduleRefresh).
  async selectionChanged() {
    const perModel = new Map();
    for (const item of this.viewer._selection) {
      const model = this.viewer._models.get(item.modelId);
      if (!model || !model.streamed) continue;
      if (!perModel.has(model)) perModel.set(model, []);
      perModel.get(model).push(Number(item.expressId));
    }
    await this.setStaticSelection(Array.from(perModel, ([model, expressIds]) => ({ model, expressIds })));
    this.scheduleRefresh();
  }

  // --- Vlastnosti ------------------------------------------------------------------
  // Plné vlastnosti z properties indexu ingestu (props-settings.json + dávky), líně a
  // jen jednou na model. Bez indexu zůstává k dispozici light-index / serverový endpoint.
  ensureProperties(model) {
    const stream = model.stream;
    if (stream.propsAttach) return stream.propsAttach;
    stream.propsAttach = (async () => {
      try {
        const ps = await fetchJson(stream.base + "props-settings.json");
        if (!ps || !ps.ids) return false;
        const ids = new Map();
        for (const key in ps.ids) ids.set(parseInt(key, 10), ps.ids[key]);
        const types = new Map();
        for (const key in ps.types || {}) types.set(parseInt(key, 10), ps.types[key]);
        const baseFileName = String(stream.settings.globalDataFileId || "").replace("-global", "-properties");
        FRAG.FragmentsGroup.url = "";
        model.group.streamSettings = { baseFileName, ids, types };
        if (ps.indexesFile && this.core.indexer) {
          const response = await fetch(scopedUrl(stream.base, ps.indexesFile), FETCH);
          const text = response.ok ? await response.text() : null;
          if (text) this.core.indexer.setRelationMap(model.group, this.core.indexer.getRelationsMapFromJSON(text));
        }
        return !!model.group.hasProperties;
      } catch (_) { return false; }
    })();
    return stream.propsAttach;
  }

  // Minimální vlastnosti z light-indexu, doplněné o sady vlastností ze serverového
  // endpointu IFC Access (pokud je k dispozici a známe zdrojový IFC).
  async fallbackProperties(model, expressId, globalId) {
    const out = {
      modelId: model.modelId,
      globalId,
      expressId,
      ifcClass: model.stream.index.classOf.get(expressId) || null,
      name: model.stream.index.nameOf.get(expressId) || null,
      description: null,
      objectType: null,
      predefinedType: null,
      attributes: { GlobalId: globalId },
      propertySets: {},
      quantities: {},
      classification: [],
    };
    if (out.name) out.attributes.Name = out.name;
    const ifcUrl = model.stream.ifcUrl;
    if (!ifcUrl || typeof location === "undefined") return out;
    try {
      const source = new URL(ifcUrl, location.href);
      if (source.origin !== location.origin) return out;
      const url = "/api/access/element-properties?file=" + encodeURIComponent(source.pathname)
        + "&globalId=" + encodeURIComponent(globalId);
      const response = await fetch(url, { credentials: "same-origin", cache: "no-store", headers: { Accept: "application/json" } });
      if (!response.ok) return out;
      const data = await response.json();
      if (data && data.name && !out.name) { out.name = data.name; out.attributes.Name = data.name; }
      if (data && data.ifcClass && !out.ifcClass) out.ifcClass = String(data.ifcClass).toUpperCase();
      for (const pset of (data && Array.isArray(data.psets) ? data.psets : [])) {
        const entries = Object.entries((pset && (pset.props || pset.properties)) || {});
        if (!entries.length) continue;
        const bucket = {};
        for (const [key, value] of entries) bucket[key] = val(value) ?? (typeof value === "object" ? null : value);
        out.propertySets[(pset && pset.name) || "Pset"] = bucket;
      }
    } catch (_) { /* endpoint je volitelný */ }
    return out;
  }

  // --- Vyhledávání nad light-indexem ------------------------------------------------
  // Rychlé dotazy (globalId / ifcClass / name) bez stahování vlastností. Vrací pole
  // expressID kandidátů, nebo null, pokud dotaz potřebuje plné vlastnosti.
  findCandidates(model, query) {
    const index = model.stream.index;
    let ids;
    if (query.globalId) {
      const eid = model.ids.globalToExpress.get(query.globalId);
      ids = eid == null ? [] : [eid];
    } else if (query.ifcClass) {
      const wanted = String(query.ifcClass).toUpperCase();
      ids = [];
      for (const [id, cls] of index.classOf) if (cls === wanted) ids.push(id);
    } else {
      ids = Array.from(model.ids.expressToGlobal.keys());
    }
    if (query.ifcClass && query.globalId) {
      const wanted = String(query.ifcClass).toUpperCase();
      ids = ids.filter((id) => index.classOf.get(id) === wanted);
    }
    if (query.name != null) ids = ids.filter((id) => index.nameOf.get(id) === query.name);
    return ids;
  }

  // --- Podlaží a strom z light-indexu -----------------------------------------------
  storeyEntries(model) {
    const light = model.stream.light;
    const byStorey = new Map();
    for (const [globalId, info] of Object.entries(light.elements || {})) {
      if (!info || !info.storey) continue;
      if (!byStorey.has(info.storey)) byStorey.set(info.storey, []);
      byStorey.get(info.storey).push({ globalId, expressId: Number(info.expressID) });
    }
    const entries = [];
    for (const storey of light.storeys || []) {
      const members = byStorey.get(storey.globalId) || [];
      const elevation = storey.elevation != null ? Number(storey.elevation)
        : (storey.elevationMeters != null ? Number(storey.elevationMeters) : null);
      const expressId = storey.expressID != null ? Number(storey.expressID) : model.ids.globalToExpress.get(storey.globalId);
      entries.push({
        modelId: model.modelId,
        expressId: expressId == null ? null : expressId,
        globalId: storey.globalId || null,
        name: storey.name || ("#" + (expressId == null ? "?" : expressId)),
        elevation: Number.isFinite(elevation) ? elevation : null,
        objects: members.map((m) => ({ modelId: model.modelId, globalId: m.globalId })),
        _expressIds: new Set(members.map((m) => m.expressId).filter(Number.isFinite)),
      });
    }
    return entries;
  }

  spatialTree(model, mode) {
    const light = model.stream.light;
    const toNode = (node) => {
      const expressId = node.expressID != null ? Number(node.expressID) : null;
      const globalId = node.globalId || (expressId != null ? model.ids.expressToGlobal.get(expressId) : null) || null;
      const storey = String(node.type || "").toUpperCase() === "IFCBUILDINGSTOREY"
        ? (light.storeys || []).find((s) => s.globalId === globalId) : null;
      const elevation = storey ? (storey.elevation != null ? Number(storey.elevation) : storey.elevationMeters) : null;
      return {
        modelId: model.modelId,
        expressId,
        globalId,
        ifcClass: node.type ? String(node.type).toUpperCase() : (expressId != null ? model.stream.index.classOf.get(expressId) || null : null),
        name: node.name || null,
        elevation: Number.isFinite(Number(elevation)) && elevation != null ? Number(elevation) : null,
        children: [],
      };
    };
    const convert = (node, filter) => {
      const children = [];
      for (const child of node.children || []) {
        const converted = convert(child, filter);
        if (converted) children.push(converted);
      }
      if (filter && !filter(node, children)) return null;
      const out = toNode(node);
      out.children = children;
      return out;
    };

    if (mode === "floor") {
      return this.storeyEntries(model).map((entry) => ({
        modelId: model.modelId,
        expressId: entry.expressId,
        globalId: entry.globalId,
        ifcClass: "IFCBUILDINGSTOREY",
        name: entry.name,
        elevation: entry.elevation,
        children: entry.objects.map((ref) => {
          const expressId = model.ids.globalToExpress.get(ref.globalId);
          return {
            modelId: model.modelId, expressId: expressId == null ? null : expressId, globalId: ref.globalId,
            ifcClass: expressId == null ? null : model.stream.index.classOf.get(expressId) || null,
            name: expressId == null ? null : model.stream.index.nameOf.get(expressId) || null,
            elevation: null, children: [],
          };
        }),
      })).sort((a, b) => (a.elevation ?? 0) - (b.elevation ?? 0));
    }

    // component: jen dekompozice — prostorové uzly a uzly s potomky (light-index nenese
    // relaci IsDecomposedBy; jde o stejný odvozený pohled jako v hlavním vieweru).
    const filter = mode === "component"
      ? (node, children) => SPATIAL_TYPES.has(String(node.type || "").toUpperCase()) || children.length > 0
      : null;
    const roots = (light.tree || []).map((root) => convert(root, filter) || toNode(root));
    if (!roots.length) return null;
    return roots.length === 1 ? roots[0] : roots;
  }

  dispose() {
    if (this._refreshTimer != null) clearTimeout(this._refreshTimer);
    this._refreshTimer = null;
    this._clearColorOverlay();
    for (const material of this._colorMaterials.values()) { try { material.dispose(); } catch (_) {} }
    this._colorMaterials.clear();
  }
}
